import type {
  BrowserConfig,
  FeatureBrowserUsageReport,
  FeatureStorageConfig,
  FeatureStorageUsageReport,
} from "@coms-platform/feature-contract";
import type {
  DevelopmentIssueLifecycleAdoptionPayload,
  DevelopmentIssueDecisionPayload,
  DevelopmentIssueVerificationInput,
  DevelopmentIssueReport,
  IssueTimelineEvent,
  IssueAnalysis
} from "@coms-platform/development-issue-contract";

export type PlatformPermission =
  | "feature.use"
  | "feature.upload"
  | "feature.update"
  | "feature.submit_review"
  | "feature.withdraw"
  | "feature.review"
  | "feature.publish"
  | "feature.archive"
  | "feature.disable"
  | "feature.enable"
  | "feature.delete"
  | "feature.storage.view"
  | "feature.storage.export"
  | "feature.storage.refresh"
  | "feature.storage.block_release"
  | "feature.storage.confirm_destructive_migration"
  | "feature.storage.purge"
  | "platform.user.read"
  | "platform.role.manage"
  | "platform.audit.read"
  | "support.issue.create"
  | "support.issue.read_own"
  | "support.issue.read_all"
  | "support.issue.manage"
  | "support.issue.purge"
  | "platform.notification.read"
  | "platform.notification.retry"
  | "platform.notification.test";

export type PlatformRole =
  | "FEATURE_USER"
  | "FEATURE_DEVELOPER"
  | "FEATURE_REVIEWER"
  | "FEATURE_PUBLISHER"
  | "PLATFORM_ADMIN";

export interface CurrentUser {
  email: string;
  display_name: string | null;
  roles: PlatformRole[];
  permissions: PlatformPermission[];
  preferred_locale: "zh-CN" | "en";
}

export type FeatureAccessMode = "ALL_AUTHENTICATED" | "RESTRICTED";

export interface FeatureAccessSettings {
  access_mode: FeatureAccessMode;
  access_revision: number;
  members: string[];
  can_restrict: boolean;
}

export interface Feature {
  access_mode: FeatureAccessMode;
  can_use: boolean;
  can_manage_access: boolean;
  feature_key: string;
  name: string;
  description: string;
  version: string;
  worker_url: string | null;
  public_url: string | null;
  worker_name: string;
  worker_ownership_state: "UNVERIFIED" | "ABSENT" | "VERIFIED" | "CONFLICT";
  worker_identity_suffix: string | null;
  status:
    | "UPLOADING"
    | "VALIDATING"
    | "DEPLOYING"
    | "DEPLOYED"
    | "FAILED"
    | "DISABLED";
  publication_state: "UNPUBLISHED" | "PUBLISHED" | "DISABLED";
  published_release_id: string | null;
  developer_email: string | null;
  artifact_hash: string;
  updated_at: string;
  manifest: { capabilities: string[] };
}

export interface Deployment {
  id: string;
  feature_key: string;
  version: string;
  worker_url: string | null;
  status: Feature["status"];
  artifact_hash: string | null;
  error_message: string | null;
  created_at: string;
  completed_at: string | null;
}

export interface FeatureRelease {
  id: string;
  feature_key: string;
  version: string;
  artifact_hash: string;
  state:
    | "DRAFT"
    | "IN_REVIEW"
    | "APPROVED"
    | "REJECTED"
    | "PUBLISHING"
    | "PUBLISHED"
    | "FAILED";
  disposition: "ACTIVE" | "WITHDRAWN" | "SUPERSEDED";
  disposition_at: string | null;
  disposition_by_email_normalized: string | null;
  disposition_reason: string | null;
  superseded_by_release_id: string | null;
  archived_at: string | null;
  archived_by_email_normalized: string | null;
  archive_reason: string | null;
  lifecycle_revision: number;
  uploaded_by_email: string;
  uploaded_at: string;
  reviewed_by_email: string | null;
  reviewed_at: string | null;
  review_note: string | null;
  published_by_email: string | null;
  published_at: string | null;
  error_message: string | null;
  publish_failure_code: string | null;
  publish_failure_stage:
    | "ARTIFACT_VALIDATION"
    | "WORKER_DEPLOYMENT"
    | "ROUTE_ACTIVATION"
    | "BROWSER_SMOKE"
    | "PUBLIC_SMOKE"
    | "FINALIZATION"
    | "UNKNOWN"
    | null;
  publish_failure_category:
    | "RETRYABLE_PLATFORM"
    | "FEATURE_FIX_REQUIRED"
    | "MANUAL_RECOVERY_REQUIRED"
    | "PLATFORM_CONFIGURATION_REQUIRED"
    | null;
  publish_failure_message: string | null;
  publish_failure_developer_message: string | null;
  publish_failure_retryable: 0 | 1 | null;
  publish_failure_attempt: number;
  publish_failure_revision: number;
  publish_failed_at: string | null;
  outbound_report_hash: string | null;
  outbound_policy_hash: string | null;
  reviewed_outbound_report_hash: string | null;
  browser_usage_report_hash: string | null;
  reviewed_browser_usage_report_hash: string | null;
  storage_usage_report_hash?: string | null;
  reviewed_storage_usage_report_hash?: string | null;
  manifest_hash?: string | null;
  build_report_hash?: string | null;
  migration_set_hash?: string | null;
  risk_snapshot_hash?: string | null;
  reviewed_risk_snapshot_hash?: string | null;
  acknowledged_risk_codes?: string[];
  outbound_summary: FeatureOutboundSummary | null;
  outbound_report?: FeatureNetworkReport | null;
  browser_usage_report?: FeatureBrowserUsageReport | null;
  manifest: {
    schema_version: "1.0" | "1.1" | "1.2" | "1.3";
    name: string;
    description: string;
    capabilities: string[];
    sdk_version: string;
    outbound?: FeatureOutboundEndpoint[];
    browser?: BrowserConfig;
    storage?: FeatureStorageConfig;
  };
}

export type ReleaseRiskSeverity = "BLOCKER" | "HIGH" | "WARNING" | "INFO";
export type ReleaseRiskCategory =
  | "COMS"
  | "OUTBOUND"
  | "BROWSER"
  | "STORAGE"
  | "MIGRATION"
  | "PACKAGE"
  | "DEPLOYMENT";

export interface ReleaseReviewFinding {
  code: string;
  severity: ReleaseRiskSeverity;
  category: ReleaseRiskCategory;
  title: string;
  summary: string;
  evidenceSource:
    | "manifest"
    | "build-report"
    | "network-report"
    | "browser-report"
    | "storage-report"
    | "platform-observation";
  evidencePointer: string;
  changedFromPublished: boolean;
  blocksApproval: boolean;
}

export interface ReleaseFindingDecisionNote {
  risk_code: string;
  note: string;
}

export interface ReleaseReviewDetail {
  risk_snapshot: {
    risk_snapshot_hash: string;
    manifest_hash: string;
    artifact_hash: string;
    migration_set_hash: string;
    findings: ReleaseReviewFinding[];
    severity_counts: Record<ReleaseRiskSeverity, number>;
    required_acknowledgements: string[];
  } | null;
  changes: {
    baseline_version: string | null;
    first_release: boolean;
    storage_changed: boolean;
    changed_finding_codes: string[];
  };
  storage_diff: {
    addedResources: Array<"d1" | "r2">;
    removedResources: Array<"d1" | "r2">;
    addedNamespaces: string[];
    removedNamespaces: string[];
    scopeReplacements: Array<{
      removedNamespace: string;
      addedNamespace: string;
      migrationFiles: string[];
    }>;
    addedContentTypes: string[];
    removedContentTypes: string[];
    browserPersistenceChanged: boolean;
    retentionChanged: boolean;
    pendingMigrations: string[];
    destructiveMigrations: string[];
  };
  package_evidence: {
    package_size_bytes: number;
    worker_bundle_size_bytes: number;
    artifact_hash: string;
    worker_bundle_hash: string;
    manifest_hash: string | null;
    build_report_hash: string | null;
    outbound_report_hash: string | null;
    browser_report_hash: string | null;
    storage_report_hash: string | null;
    migration_set_hash: string | null;
    schema_version: string;
    sdk_version: string;
    toolchain_version: string | null;
    source_hash: string | null;
  };
  deployment_plan: {
    worker_name: string;
    route_path: string;
    bindings: string[];
    storage_resources: Array<{
      resource_type: "D1" | "R2";
      enabled: boolean;
      action: "NONE" | "CREATE" | "REUSE" | "RESTORE" | "RETAIN";
      resource_name: string | null;
      status: string;
      private: true | null;
    }>;
  };
  storage: {
    usage_report: FeatureStorageUsageReport | null;
    resources: Array<{
      id: string;
      feature_key: string;
      storage_generation: number;
      owner_email_normalized: string;
      resource_type: "D1" | "R2";
      resource_name: string;
      status: string;
      data_scope_summary: string;
      created_at: string;
      last_observed_at: string | null;
      retained_at: string | null;
      purged_at: string | null;
      error_message: string | null;
    }>;
    migrations: Array<{
      id: string;
      feature_key: string;
      release_id: string;
      migration_version: string;
      filename: string;
      checksum: string;
      destructive: 0 | 1;
      status: string;
      confirmed_by_email_normalized: string | null;
      confirmed_at: string | null;
      decision_note: string | null;
      applied_at: string | null;
      error_message: string | null;
      sql: string;
      confirmation_fingerprint: string | null;
    }>;
    active_release_blocks: Array<{
      id: string;
      reason_code: string;
      reason_detail: string;
      created_at: string;
    }>;
    latest_observations: Array<{
      id: string;
      resource_type: "D1" | "R2";
      observed_at: string;
      storage_bytes: number | null;
      object_count: number | null;
      rows_read: number | null;
      rows_written: number | null;
      operation_count: number | null;
      health_status: "HEALTHY" | "WARNING" | "ERROR" | "UNKNOWN";
    }>;
  };
  audit_trail: Array<{
    id: string;
    actor_email_normalized: string | null;
    actor_type: "COMS_USER" | "SYSTEM";
    action: string;
    outcome: "SUCCEEDED" | "FAILED";
    created_at: string;
    review_decision: string | null;
    decision_note: string | null;
    finding_decision_notes: ReleaseFindingDecisionNote[];
  }>;
}

export interface FeatureOutboundSummary {
  origins: number;
  request_sites: number;
  post_requests: number;
  coms_derived_requests: number;
  unresolved_requests: 0;
}

export interface FeatureOutboundEndpoint {
  id: string;
  origin: string;
  methods: Array<"GET" | "HEAD" | "POST">;
  path_prefixes: string[];
  purpose: string;
  data_classification: "none" | "user_input" | "coms_derived" | "mixed";
}

export interface FeatureNetworkRequest {
  endpoint_id: string;
  origin: string;
  path_pattern: string;
  method: "GET" | "HEAD" | "POST";
  purpose: string;
  data_classification: "none" | "user_input" | "coms_derived" | "mixed";
  source: string;
  timeout_ms: number;
  redirect: "manual";
  header_names: string[];
}

export interface FeatureNetworkReport {
  schema_version: "1.0";
  feature_key: string;
  version: string;
  policy_hash: string;
  summary: FeatureOutboundSummary;
  requests: FeatureNetworkRequest[];
  unresolved_requests: [];
}

export interface PublishRouteRecovery {
  feature_key: string;
  zone_id: string;
  route_id: string;
  route_pattern: string;
  worker_name: string;
  public_url: string;
  confirmation_fingerprint: string;
}

export interface PlatformUser {
  email: string;
  display_name: string | null;
  status: "ACTIVE" | "DISABLED";
  roles: PlatformRole[];
  permissions: PlatformPermission[];
  configured_admin: boolean;
  first_login_at: string;
  last_login_at: string;
}

export interface AuditEvent {
  id: string;
  actor_email: string | null;
  actor_type: "COMS_USER" | "SYSTEM";
  action: string;
  target_type: string;
  target_id: string;
  outcome: "SUCCEEDED" | "FAILED";
  details_json: string | null;
  created_at: string;
}

export interface NotificationListItem {
  id: string;
  event_id: string;
  event_type: string;
  target_type: string;
  target_id: string;
  target_revision: string;
  recipient_kind: string;
  recipient_email_masked: string | null;
  recipient_locale: "zh-CN" | "en";
  template_key: string;
  status: string;
  event_status: string;
  outbox_state: string | null;
  attempt_count: number;
  provider: string;
  created_at: string;
  last_attempt_at: string | null;
}

export interface NotificationHealth {
  enabled: boolean;
  provider: string;
  sending_domain: string | null;
  email_binding: string;
  queue_binding: string;
  dlq_consumer: string;
  template_preview: "ENABLED" | "DISABLED";
  pending_outbox: number;
  dead_lettered_outbox: number;
  oldest_pending_at: string | null;
  oldest_pending_age_seconds: number | null;
  failed_deliveries: number;
  suppressed_recipients: number;
  no_recipient_events: number;
  last_successful_send_at: string | null;
  last_delivery_event_at: string | null;
  delivery_status_counts: {
    queued: number;
    accepted: number;
    delivered: number;
    deferred: number;
    bounced: number;
    complained: number;
    failed: number;
    suppressed: number;
  };
  average_delivery_delay_seconds: number | null;
  delivery_rate_by_template: Array<{
    template_key: string;
    total_count: number;
    delivered_count: number;
    delivery_rate: number;
  }>;
  failure_rate_by_provider_error: Array<{
    error_code: string;
    failure_count: number;
    total_count: number;
    failure_rate: number;
  }>;
  alerts: Array<{
    code: string;
    severity: "WARNING" | "CRITICAL";
  }>;
}

export const notificationTemplateKeys = [
  "development-issue-submitted-admin-v1",
  "development-issue-admin-replied-user-v1",
  "feature-release-submitted-reviewer-v1",
  "feature-release-approved-developer-v1",
  "feature-release-approved-publisher-v1",
  "feature-release-rejected-developer-v1",
  "feature-release-published-developer-v1",
  "feature-release-published-publisher-v1",
  "feature-release-publish-failed-developer-v1",
  "feature-release-publish-failed-publisher-v1",
  "platform-test-admin-v1"
] as const;

export type NotificationTemplateKey = (typeof notificationTemplateKeys)[number];

export interface NotificationTemplatePreview {
  template_key: NotificationTemplateKey;
  locale: "zh-CN" | "en";
  viewport_width: 640;
  subject: string;
  html: string;
  text: string;
}

export interface NotificationTargetSummaryItem {
  event_type: string;
  recipient_kind: string;
  recipient_count: number;
  queued_count: number;
  accepted_count: number;
  delivered_count: number;
  failed_count: number;
  latest_status: string;
  latest_created_at: string;
}

export interface NotificationDetail {
  delivery: Omit<NotificationListItem, "recipient_email_masked"> & {
    recipient_email_normalized: string | null;
    recipient_email_hash: string;
    provider_message_id: string | null;
    last_error_code: string | null;
  };
  event: {
    id: string;
    event_type: string;
    target_type: string;
    target_id: string;
    target_revision: string;
    status: string;
    created_at: string;
  };
  outbox: { state: string; last_error_code: string | null } | null;
  attempts: Array<{
    id: string;
    attempt_number: number;
    outcome: string;
    provider_error_code: string | null;
    started_at: string;
  }>;
  recipient_suppressed: boolean;
}

export interface DevelopmentIssue {
  id: string;
  client_report_id: string;
  target_scope: "FEATURE" | "WORKSPACE";
  attempted_feature_key: string | null;
  issue_type:
    | "GENERAL_FEATURE_DEVELOPMENT"
    | "COMS_AGENT_API_DATA_REQUIREMENT";
  feature_key: string | null;
  feature_version: string | null;
  feature_record_state: "LOCAL_ONLY" | "PLATFORM_OWNED" | null;
  title: string | null;
  stage: string;
  category: string;
  severity: "BLOCKING" | "DEGRADED" | "QUESTION";
  status:
    | "SUBMITTED"
    | "TRIAGED"
    | "IN_ANALYSIS"
    | "ACTION_PLANNED"
    | "POTENTIALLY_RESOLVED"
    | "RESOLVED"
    | "DUPLICATE"
    | "ARCHIVED"
    | "WITHDRAWN"
    | "PURGED";
  issue_fingerprint: string;
  requirement_fingerprint: string | null;
  skill_name: string | null;
  skill_pack_version: string;
  toolchain_version: string;
  agent_kind: string | null;
  sdk_version: string | null;
  primary_error_codes_json: string;
  reporter_email_normalized: string | null;
  artifact_state: "AVAILABLE" | "EXPIRED" | "WITHDRAWN" | "PURGED";
  secret_scan_status: "PASSED" | "BLOCKED";
  pii_warning_count: number;
  platform_snapshot_id_at_upload: string | null;
  snapshot_relation: string | null;
  suspected_issue_type: string | null;
  suspected_owner: string | null;
  evidence_level: string | null;
  triage_result: string | null;
  created_at: string;
  updated_at: string;
  resolved_at: string | null;
  resolved_snapshot_id: string | null;
  resolved_skill_pack_version: string | null;
  resolved_sdk_version: string | null;
  validation_method: string | null;
  retention_expires_at: string;
  archive_reason: string | null;
  evidence_revision: number;
}

export interface DevelopmentIssueAnalysis {
  id: string;
  issue_id: string;
  root_cause_category: string;
  affected_component: string;
  impact: string | null;
  recommended_action: string | null;
  target_type: string;
  target_version: string | null;
  decision_note: string | null;
  created_by_email_normalized: string | null;
  created_at: string;
}

export interface DevelopmentIssueReply {
  id: string;
  issue_id: string;
  author_email_normalized: string;
  body_markdown: string;
  safe_excerpt: string;
  created_at: string;
  source_decision_id?: string | null;
}

export interface DevelopmentIssueDetail {
  issue: DevelopmentIssue;
  report?: DevelopmentIssueReport;
  timeline?: IssueTimelineEvent[];
  analyses?: DevelopmentIssueAnalysis[];
  replies?: DevelopmentIssueReply[];
  linked_candidates?: LinkedImprovementCandidate[];
  notification_summary?: NotificationTargetSummaryItem[];
  similar_issue_count?: number;
  similar_requirement_count?: number;
  confirmed_decision?: {
    id: string;
    outcome: "INVESTIGATE" | "GUIDANCE" | "IMPROVE" | "DUPLICATE";
    summary: string;
    triage_result: string | null;
    analyses: Array<Omit<DevelopmentIssueAnalysis, "id" | "issue_id" | "created_by_email_normalized" | "created_at"> & { entry_id: string }>;
    verification_plan: {
      scope: string;
      acceptance_criteria: string[];
      expected_methods: string[];
    } | null;
    confirmed_at: string;
  } | null;
}

export interface DevelopmentIssueInternalAnalysis {
  id: string;
  issue_id: string;
  schema_version: "1.0" | "1.1";
  base_evidence_revision: string;
  evidence_snapshot_hash: string;
  analysis_scope:
    | "REPORT_ONLY"
    | "REPORT_AND_SOURCE"
    | "REPORT_SOURCE_AND_OFFLINE_TEST";
  output_locale: "zh-CN" | "en";
  analysis_content_hash: string;
  analysis: IssueAnalysis;
  visibility: "INTERNAL";
  analysis_origin: "AI_ASSISTED";
  review_state: "AI_DRAFT" | "HUMAN_REVIEWED";
  created_by_email_normalized: string;
  created_at: string;
  reviewed_by_email_normalized: string | null;
  reviewed_at: string | null;
  dismissed_at?: string | null;
  dismissed_by_email_normalized?: string | null;
  expires_at: string;
  platform_evidence_state: "CURRENT" | "STALE" | "SOURCE_UNAVAILABLE";
  platform_evidence_reason:
    | "EVIDENCE_REVISION_CHANGED"
    | "SOURCE_ARTIFACT_UNAVAILABLE"
    | "CURRENT_PUBLISHED_SELECTION_CHANGED"
    | "PLATFORM_EVIDENCE_UNAVAILABLE"
    | null;
  local_source_state: "CLIENT_RECORDED" | "NOT_INCLUDED";
  adoption_state: "NOT_APPLIED" | "APPLIED";
  adoption: {
    id: string;
    adopted_payload_hash: string;
    triage_result: string | null;
    lifecycle_analysis_ids: string[];
    applied_by_email_normalized: string;
    applied_at: string;
  } | null;
}

export interface DevelopmentIssueLifecycleAdoptionPreview {
  analysis_id: string;
  analysis_content_hash: string;
  evidence_revision: string;
  current_status: DevelopmentIssue["status"];
  resulting_status: DevelopmentIssue["status"];
  current_triage_result: string | null;
  selected_triage_result: DevelopmentIssueLifecycleAdoptionPayload["triage_result"];
  existing_lifecycle_analysis_count: number;
  selected_lifecycle_analysis_count: number;
  payload: DevelopmentIssueLifecycleAdoptionPayload;
  adoption_content_hash: string;
  user_visible_fields: string[];
  reply_side_effect: "NONE";
  notification_side_effect: "NONE";
  resolve_side_effect: "NONE";
}

export interface LinkedImprovementCandidate {
  id: string;
  title: string;
  target_type: string;
  priority: ImprovementCandidate["priority"];
  status: ImprovementCandidate["status"];
  planned_version: string | null;
  released_version: string | null;
}

export interface ImprovementCandidateIssueSummary {
  id: string;
  title: string | null;
  status: DevelopmentIssue["status"];
  severity: string;
  created_at: string;
  evidence_revision?: number;
  required_for_resolution?: 0 | 1;
}

export interface DevelopmentIssueMetrics {
  total: number;
  new_issues: number;
  blocking_issues: number;
  unresolved_over_7_days: number;
  issues_seen_after_fix: number;
  supported_by_current_api: number;
  agent_or_skill_execution_gaps: number;
  top_error_codes: Array<{ value: string; count: number }>;
  top_skills: Array<{ value: string; count: number }>;
  top_skill_pack_versions: Array<{ value: string; count: number }>;
  repeated_issue_fingerprints: Array<{ value: string; count: number }>;
  repeated_requirement_fingerprints: Array<{ value: string; count: number }>;
  top_missing_capabilities: Array<{ value: string; count: number }>;
  top_missing_views: Array<{ value: string; count: number }>;
  top_missing_fields: Array<{ value: string; count: number }>;
  top_missing_relations: Array<{ value: string; count: number }>;
  top_missing_operators: Array<{ value: string; count: number }>;
  top_missing_aggregations: Array<{ value: string; count: number }>;
  issues_by_snapshot: Array<{ value: string; count: number }>;
}

export interface ImprovementCandidate {
  id: string;
  title: string;
  target_type: string;
  priority: "LOW" | "MEDIUM" | "HIGH" | "CRITICAL";
  status: "OPEN" | "PLANNED" | "IN_PROGRESS" | "RELEASED" | "VERIFIED" | "CANCELED";
  description: string;
  issue_ids_json: string;
  issue_fingerprints_json: string;
  requirement_fingerprints_json: string;
  planned_version: string | null;
  released_version: string | null;
  planned_snapshot_id: string | null;
  released_snapshot_id: string | null;
  owner_email_normalized: string | null;
  created_by_email_normalized: string;
  created_at: string;
  updated_at: string;
  released_at: string | null;
  verified_at: string | null;
  revision: number;
  release_summary: string | null;
  linked_issue_count?: number;
  linked_issues?: ImprovementCandidateIssueSummary[];
  verification_context?: VerificationContext;
  latest_verification?: DevelopmentIssueVerification | null;
}

export interface VerificationContext {
  source_decision_id: string | null;
  acceptance_criteria: string[];
  expected_methods: DevelopmentIssueVerificationInput["method"][];
  required_version_refs: DevelopmentIssueVerificationInput["version_refs"];
  candidate_versions: Array<{ id: string; revision: number }>;
  ready: boolean;
  context_hash: string;
}

export interface DevelopmentIssueDecision {
  id: string;
  issue_id: string;
  state: "DRAFT" | "CONFIRMED";
  origin: "MANUAL" | "AI_ASSISTED" | "LEGACY";
  draft_revision: number;
  base_evidence_revision: number;
  base_current_decision_id: string | null;
  handled_internal_analysis_id: string | null;
  source_internal_analysis_id: string | null;
  source_analysis_content_hash: string | null;
  confirmed_content_hash: string | null;
  result_evidence_revision: number | null;
  created_at: string;
  updated_at: string;
  confirmed_at: string | null;
  draft_expires_at: string;
  payload: DevelopmentIssueDecisionPayload;
  result: {
    analysis_ids: string[];
    candidate_by_entry_id: Record<string, string>;
    final_issue_status: DevelopmentIssue["status"];
  } | null;
}

export interface DevelopmentIssueReplyDraft {
  id: string;
  source_decision_id: string | null;
  body_text: string;
  locale: "zh-CN" | "en";
  draft_revision: number;
  base_evidence_revision: number;
  updated_at: string;
  expires_at: string;
  basis_summary: Record<string, unknown>;
}

export interface DevelopmentIssueVerification {
  id: string;
  target_type: "ISSUE" | "IMPROVEMENT_CANDIDATE";
  issue_id: string | null;
  candidate_id: string | null;
  source_decision_id: string | null;
  target_revision: number;
  method: DevelopmentIssueVerificationInput["method"];
  scope: string;
  result: "PASSED" | "FAILED" | "INCONCLUSIVE" | "NOT_RUN";
  criteria_results: Array<{
    criterion: string;
    result: "PASSED" | "FAILED" | "INCONCLUSIVE" | "NOT_RUN";
    note: string | null;
  }>;
  version_refs: DevelopmentIssueVerificationInput["version_refs"];
  checked_context_hash: string;
  safe_summary: string;
  created_at: string;
}

export interface DevelopmentIssueWorkbench {
  issue: Pick<
    DevelopmentIssue,
    "id" | "title" | "target_scope" | "severity" | "status" | "updated_at"
  > & { evidence_revision: string };
  current_decision: DevelopmentIssueDecision | null;
  decision_draft: DevelopmentIssueDecision | null;
  reply_draft: DevelopmentIssueReplyDraft | null;
  linked_candidates: Array<{
    issue_id: string;
    candidate_id: string;
    required_for_resolution: 0 | 1;
    title: string;
    target_type: string;
    priority: ImprovementCandidate["priority"];
    status: ImprovementCandidate["status"];
    revision: number;
    planned_version: string | null;
    released_version: string | null;
    released_snapshot_id: string | null;
  }>;
  latest_verification: DevelopmentIssueVerification | null;
  next_action: string;
  verification_context: VerificationContext;
  legacy_context: boolean;
  available_actions: {
    create_decision: boolean;
    send_reply: boolean;
    verify: boolean;
    resolve: boolean;
  };
  internal_analysis_summary?: { latest_id: string | null; needs_review: boolean };
}

export interface DevelopmentIssueQueueItem {
  id: string;
  title: string | null;
  target_scope: string;
  feature_key: string | null;
  severity: string;
  status: DevelopmentIssue["status"];
  created_at: string;
  updated_at: string;
  evidence_revision: string;
  queues: string[];
  next_action: string;
  blocked_since: string;
  linked_candidate_progress: { outstanding: boolean; canceled: boolean; total: number; required: number };
}

export interface Capability {
  name: string;
  method: "GET" | "POST";
  path: string;
  readOnly: boolean;
  schemaVersion?: string | null;
}

export interface SchemaView {
  name: string;
  description?: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly params: Record<string, string | number | null> = {}
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export async function api<T>(
  path: string,
  init: RequestInit = {},
  options: { csrfToken?: string } = {}
): Promise<T> {
  if (!path.startsWith("/api/v1/")) {
    throw new Error("Platform path is outside the v1 API.");
  }
  const headers = new Headers(init.headers);
  headers.set("accept", "application/json");
  if (options.csrfToken) {
    headers.set("x-csrf-token", options.csrfToken);
  }
  const response = await fetch(path, {
    ...init,
    headers,
    credentials: "same-origin",
    redirect: "manual"
  });
  if (response.status >= 300 && response.status < 400) {
    throw new ApiError(
      response.status,
      "UNEXPECTED_REDIRECT",
      "Platform returned an unexpected redirect."
    );
  }
  if (response.status === 204) return undefined as T;
  const payload = (await response.json().catch(() => null)) as
    | {
        error?: {
          code?: string;
          message?: string;
          params?: Record<string, string | number | null>;
        };
      }
    | T
    | null;
  if (!response.ok) {
    const error =
      payload && typeof payload === "object" && "error" in payload
        ? payload.error
        : undefined;
    throw new ApiError(
      response.status,
      error?.code ?? `HTTP_${response.status}`,
      error?.message ?? `Request failed (${response.status})`,
      error?.params ?? {}
    );
  }
  return payload as T;
}
